import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API, { endPoint } from '../utils/apiconfig';
import { getApiErrorMessage } from '../utils/formErrors';
import {
  PAYMENT_PROOF_ACCEPT,
  PAYMENT_PROOF_HINT,
  PAYMENT_PROOF_REJECT_MESSAGE,
  isAllowedPaymentProofFile,
  isPaymentProofImage,
} from '../utils/paymentProofFile';
import { formatAppDateTime } from '../utils/dateFormat';

const publicApi = axios.create({ baseURL: `${endPoint}/api/` });

function AccountRecoveryPay() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing recovery link token. Use the link shared by admin.');
      setLoading(false);
      return;
    }
    setLoading(true);
    publicApi
      .get(API.RECOVERY_CONTEXT, { params: { token } })
      .then((r) => {
        setContext(r.data);
        if (r.data?.valid === false && r.data?.error && !r.data?.status) {
          setLoadError(r.data.error);
        }
      })
      .catch((e) => {
        setLoadError(getApiErrorMessage(e.response?.data, 'Could not load recovery details.'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const onFileSelect = (file) => {
    if (!file) return;
    if (!isAllowedPaymentProofFile(file)) {
      setUploadError(PAYMENT_PROOF_REJECT_MESSAGE);
      return;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedFile(file);
    setUploadError('');
  };

  const onSubmit = async () => {
    if (!selectedFile || !token || isUploading || !context?.can_submit) return;
    setIsUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('token', token);
    formData.append('screenshot', selectedFile);
    if (transactionId.trim()) formData.append('transaction_id', transactionId.trim());

    try {
      const res = await publicApi.post(API.RECOVERY_SUBMIT, formData);
      if (res.data?.success) {
        setSubmitted(true);
        return;
      }
      setUploadError(getApiErrorMessage(res.data));
    } catch (err) {
      setUploadError(getApiErrorMessage(err.response?.data, 'Upload failed.'));
    } finally {
      setIsUploading(false);
    }
  };

  const previewUrl =
    isPaymentProofImage(selectedFile) && selectedFile
      ? (previewUrlRef.current ||= URL.createObjectURL(selectedFile))
      : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <p className="text-sm text-slate-500">Loading payment details…</p>
      </div>
    );
  }

  if (loadError && !context) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm">
          <p className="text-red-600 text-sm mb-4">{loadError}</p>
          <Link to="/login" className="text-violet-600 text-sm font-semibold hover:underline">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  if (!context) {
    return null;
  }

  const isExpired = context.is_link_expired || context.valid === false;
  const isRejected = context.status === 'rejected';
  const isAlreadySubmitted = context.status === 'submitted';
  const isApproved = context.status === 'approved';
  const blockingMessage = context.error || (
    isExpired ? 'This recovery link has expired. Contact admin for a new link.' : null
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white rounded-3xl border border-slate-200 p-8 shadow-lg text-center">
          <div className="text-4xl mb-3">✅</div>
          <h1 className="text-xl font-black text-slate-900 mb-2">Payment submitted</h1>
          <p className="text-sm text-slate-600">
            Admin will review your screenshot. Your account will be unblocked after approval.
          </p>
        </div>
      </div>
    );
  }

  const amountTotal = context?.payment?.amount_total;
  const months = context?.payment?.months || [];
  const qrUrl = context?.qr?.qr_image_url;
  const expiresLabel = context.token_expires_at
    ? formatAppDateTime(context.token_expires_at)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-3xl bg-white border border-slate-200/80 p-6 sm:p-8 shadow-lg">
          <p className="text-[11px] font-bold tracking-[0.2em] text-indigo-600 uppercase mb-2">
            Account Recovery
          </p>
          <h1 className="text-2xl font-black text-slate-900 mb-1">Complete overdue payment</h1>
          <p className="text-sm text-slate-600 mb-4">
            Pay <strong>₹{Number(amountTotal).toLocaleString('en-IN')}</strong> to restore your account.
          </p>

          {expiresLabel && context.status === 'link_generated' && (
            <p className={`text-xs mb-4 rounded-xl border px-3 py-2 ${
              isExpired
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}>
              {isExpired
                ? 'This link expired. Ask admin to send a new payment link.'
                : `Link expires ${expiresLabel} (${context.link_valid_hours || 24} hours from creation).`}
            </p>
          )}

          {(blockingMessage || isAlreadySubmitted || isApproved) && (
            <div className={`mb-6 rounded-2xl border p-4 text-sm ${
              isAlreadySubmitted || isApproved
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}>
              {isApproved && 'This recovery payment was approved. You can log in again.'}
              {isAlreadySubmitted && !isApproved && 'Payment proof submitted. Waiting for admin review.'}
              {!isAlreadySubmitted && !isApproved && blockingMessage}
            </div>
          )}

          {months.length > 0 && (
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Covers EMI months</p>
              <ul className="space-y-1 text-sm text-amber-900">
                {months.map((m) => (
                  <li key={m.installment_no}>
                    Month {m.installment_no} — ₹{Number(m.amount).toLocaleString('en-IN')}
                    {m.due_date ? ` (due ${m.due_date})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {qrUrl && context.can_submit && (
            <div className="mb-6 flex flex-col items-center rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-500 mb-3">Scan to pay</p>
              <img src={qrUrl} alt="Payment QR" className="w-48 h-48 object-contain rounded-xl bg-white border border-slate-200" />
            </div>
          )}

          {context.can_submit ? (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">
                Transaction ID (optional)
              </label>
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm"
                placeholder="UPI / bank reference"
                disabled={!context?.can_submit}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">
                Payment screenshot
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={PAYMENT_PROOF_ACCEPT}
                className="hidden"
                onChange={(e) => onFileSelect(e.target.files?.[0])}
                disabled={!context?.can_submit}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!context?.can_submit}
                className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-600 hover:border-violet-400 hover:text-violet-600 transition disabled:opacity-40"
              >
                {selectedFile ? selectedFile.name : `Choose file (${PAYMENT_PROOF_HINT})`}
              </button>
              {previewUrl && (
                <img src={previewUrl} alt="Preview" className="mt-3 max-h-48 rounded-xl border border-slate-200" />
              )}
            </div>

            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

            <button
              type="button"
              onClick={onSubmit}
              disabled={!selectedFile || isUploading || !context?.can_submit}
              className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm disabled:opacity-40 transition"
            >
              {isUploading ? 'Submitting…' : 'Submit payment proof'}
            </button>
          </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">
              {isRejected
                ? 'This payment was rejected. Contact admin for a new recovery link.'
                : 'Upload is not available for this link.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountRecoveryPay;
